/**
 * Embeds the PCI Booking hosted card-capture iframe and resolves the captured vault token via the
 * origin-validated `CardSubmitSuccess` postMessage. The full card number never reaches Elara — only the
 * opaque token plus safe display metadata. Reused by the wallet (M4) and hotel checkout (M6).
 */

import type { CapturedCard } from '@elara/client-core/lib/pci-card-capture';

import { useEffect, useRef, useState } from 'react';

import {
  isCardSubmitSuccessMessage,
  parseCapturedCard,
  PCI_BOOKING_ORIGIN,
} from '@elara/client-core/lib/pci-card-capture';
import { cn } from '@elara/client-core/lib/utils';

import { Button, Loader, MaterialIcon } from '#ui/index.js';

// Re-exported so existing consumers keep importing the type from this component; the shape (and the
// tolerant parser) now live in the shared, platform-neutral module reused by the mobile WebView.
export type { CapturedCard } from '@elara/client-core/lib/pci-card-capture';

const DEFAULT_FRAME_HEIGHT = 520;

interface CardCaptureFrameProps {
  /** Mints a capture session (passing the page origin) and returns the iframe URL to embed. */
  mintSession: () => Promise<string>;
  /** Called once with the captured card the moment PCI Booking reports success. */
  onCaptured: (card: CapturedCard) => void | Promise<void>;
  onCancel?: () => void;
}

export function CardCaptureFrame({ mintSession, onCaptured, onCancel }: CardCaptureFrameProps) {
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [frameHeight, setFrameHeight] = useState(DEFAULT_FRAME_HEIGHT);
  const handledRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    mintSession()
      .then((url) => {
        if (!cancelled) setIframeUrl(url);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not start card capture.');
      });
    return () => {
      cancelled = true;
    };
  }, [mintSession]);

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (event.origin !== PCI_BOOKING_ORIGIN) return;
      const data = event.data as unknown;

      // PCI Booking posts "frameDimensionsChanged:WIDTH:HEIGHT" (card-capture.min.js). Size to the HEIGHT
      // (the last segment, the form's real content height) so the frame fits snugly — no trailing
      // whitespace, no clipping — and stays correct on mobile where the form is taller. Small buffer
      // covers the container's % padding.
      if (typeof data === 'string') {
        if (data.startsWith('frameDimensionsChanged')) {
          const height = Number(data.split(':').pop());
          if (Number.isFinite(height) && height >= 200 && height <= 1200) setFrameHeight(Math.ceil(height) + 24);
        }
        return;
      }
      if (!isCardSubmitSuccessMessage(data) || handledRef.current) return;

      const card = parseCapturedCard(data);
      if (!card) {
        // Log the raw payload (for diagnosing any PCI Booking field/format drift); show a friendly message.
        console.warn('[CardCaptureFrame] could not parse CardSubmitSuccess payload:', data);
        setError("We couldn't read those card details. Please try again.");
        return;
      }
      handledRef.current = true;
      setSaving(true);
      Promise.resolve(onCaptured(card)).catch((err: unknown) => {
        handledRef.current = false;
        setSaving(false);
        setError(err instanceof Error ? err.message : 'Could not save the card.');
      });
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [onCaptured]);

  return (
    <div className="flex flex-col gap-4 rounded-md border bg-background overflow-hidden p-4 pt-3">
      <div className="flex items-center justify-between gap-4">
        <h3 className="text-xl font-regular">Add a card</h3>
        {onCancel && (
          <Button
            variant="icon"
            size="icon"
            tooltip="Cancel"
            className="flex-shrink-0"
            onClick={onCancel}
            disabled={saving}
          >
            <MaterialIcon icon="close" />
          </Button>
        )}
      </div>
      {saving && <p className="text-sm text-muted-foreground">Saving card…</p>}
      {error && <p className="text-sm text-destructive">{error}</p>}
      {!iframeUrl && !error && (
        <div className="flex items-center justify-center py-8">
          <Loader loading />
        </div>
      )}
      {iframeUrl && (
        <iframe title="Add a payment card" src={iframeUrl} style={{ height: `${frameHeight}px` }} className="w-full" />
      )}
      <p className={cn('text-xs text-muted-foreground text-pretty mr-[100px]', (iframeUrl || error) && '-mt-19.5')}>
        Card details are stored securely with our payment processor. Elara never sees your full card number.
      </p>
    </div>
  );
}
